/**
 * Unter-Risiken einer Immobilie (Fachabstimmung 31.08.2026, Gebäudeerfassung
 * Punkt J): „PV-Anlage und Öltank als eigene Risikoobjekte betrachten. Der
 * Öltank zählt für die Gewässerschaden-Haftpflicht, nicht für das Gebäude.“
 *
 * Reine Logik ohne Netz: aus den Antworten EINES Gebäudes die Kind-Risiken
 * bauen (Typ, Name, Merkmale) und mit bereits vorhandenen Kindern abgleichen.
 * `NewRiskPage` verschickt das Ergebnis mit `parentRiskObjectId` = Gebäude-Id.
 *
 * Regeln:
 * - PV-Anlage: nur bei `heizungen["Solar-/Photovoltaikanlage"]` angehakt UND
 *   `art === "Photovoltaik"` (Solarthermie bleibt Teil des Gebäudes).
 * - Öltank: `umwelt["Öltanks"]` angehakt — oder eine Ölheizung (die Maske
 *   erzwingt dann den Öltank ebenfalls).
 * - Beim Bearbeiten wird ein vorhandenes Kind gleicher Art aktualisiert statt
 *   ein zweites angelegt. Entfällt PV/Öltank im Gebäude, bleibt das Kind
 *   bewusst stehen (kein Löschen) — es kann eigene Verträge/Schäden tragen.
 */
import { asRecordOfRecords } from "./structuredAnswers";

export type UnterRisikoArt = "photovoltaik" | "oeltank";

export type Merkmal = { nameBezeichnung: string; riskCharacteristicValue: string };

export type UnterRisikoEntwurf = {
  art: UnterRisikoArt;
  riskType: "Photovoltaik" | "Umwelt";
  name: string;
  characteristics: Merkmal[];
};

export type VorhandenesKind = {
  id: number;
  name?: string | null;
  riskType?: string | null;
};

export const PV_EINTRAG = "Solar-/Photovoltaikanlage";
export const OELTANK_EINTRAG = "Öltanks";
export const OELTANK_NAME_PREFIX = "Öltank";
/** Wert für `whg_tanks_art` der Umwelt-Maske (Freitextfeld „Art“). */
export const OELTANK_ART = "Heizöltank";

const ADRESS_KEYS = ["street", "postalCode", "city", "country"] as const;

/**
 * Schlüssel, die diese Logik am Kind pflegt. Beim Aktualisieren werden genau
 * diese ersetzt (auch geleert), alles andere am Kind bleibt.
 */
export const VERWALTETE_KEYS: Record<UnterRisikoArt, readonly string[]> = {
  photovoltaik: [
    "pv_leistungKwp",
    "pv_anzahl",
    "pv_baujahr",
    "pv_hersteller",
    "pv_errichter",
    "pv_kosten",
    "pv_montageort",
    "pv_svAbnahme",
    ...ADRESS_KEYS,
  ],
  oeltank: [
    "whg_tanks_vorhanden",
    "whg_tanks_art",
    "whg_tanks_liter",
    "whg_tanks_anzahl",
    "whg_tanks_standort",
    ...ADRESS_KEYS,
  ],
};

const text = (v: unknown): string => {
  if (v === undefined || v === null) return "";
  return String(v).trim();
};

const merkmale = (pairs: Array<[string, unknown]>): Merkmal[] =>
  pairs
    .map(([nameBezeichnung, v]) => ({ nameBezeichnung, riskCharacteristicValue: text(v) }))
    .filter((m) => m.riskCharacteristicValue !== "");

/** „Musterstr. 1, 12345 Musterstadt“ — leer, wenn nichts erfasst ist. */
export const adressText = (adresse: Record<string, unknown>): string =>
  [text(adresse.street), [text(adresse.postalCode), text(adresse.city)].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

/**
 * Baut die Kind-Risiken eines Gebäudes.
 * @param adresse globaler Antwort-Bag (street/postalCode/city/country)
 * @param answers Antworten des Gebäudes (`heizungen`/`umwelt` als Objekt oder JSON-String)
 * @param gebaeudeLabel Fallback für den Namen, wenn keine Adresse erfasst ist
 */
export const baueUnterRisiken = (
  adresse: Record<string, unknown>,
  answers: Record<string, unknown>,
  gebaeudeLabel?: string,
): UnterRisikoEntwurf[] => {
  const heizungen = asRecordOfRecords(answers.heizungen);
  const umwelt = asRecordOfRecords(answers.umwelt);
  const ort = adressText(adresse) || text(gebaeudeLabel);
  const adressMerkmale = merkmale(ADRESS_KEYS.map((k) => [k, adresse[k]]));
  const out: UnterRisikoEntwurf[] = [];

  const pv = heizungen[PV_EINTRAG];
  if (pv && text(pv.selected) === "true" && text(pv.art) === "Photovoltaik") {
    const kwp = text(pv.leistungKwp);
    const titel = kwp ? `Photovoltaik ${kwp} kWp` : "Photovoltaik";
    out.push({
      art: "photovoltaik",
      riskType: "Photovoltaik",
      name: ort ? `${titel} – ${ort}` : titel,
      characteristics: [
        ...merkmale([
          ["pv_leistungKwp", pv.leistungKwp],
          ["pv_anzahl", pv.anzahl],
          ["pv_baujahr", pv.baujahr],
          ["pv_hersteller", pv.hersteller],
          ["pv_errichter", pv.errichter],
          ["pv_kosten", pv.kosten],
          ["pv_montageort", pv.montageort],
          ["pv_svAbnahme", pv.svAbnahme],
        ]),
        ...adressMerkmale,
      ],
    });
  }

  const tank = umwelt[OELTANK_EINTRAG];
  const oelheizung = text(heizungen["Ölheizung"]?.selected) === "true";
  if ((tank && text(tank.selected) === "true") || oelheizung) {
    const t = tank ?? {};
    out.push({
      art: "oeltank",
      riskType: "Umwelt",
      name: ort ? `${OELTANK_NAME_PREFIX} – ${ort}` : OELTANK_NAME_PREFIX,
      characteristics: [
        // Schlüssel der Umwelt-Maske (WHG Ziffer 1.2.1 „Tanks“).
        ...merkmale([
          ["whg_tanks_vorhanden", "ja"],
          ["whg_tanks_art", OELTANK_ART],
          ["whg_tanks_liter", t.menge],
          ["whg_tanks_anzahl", t.anzahl],
          ["whg_tanks_standort", t.standort],
        ]),
        ...adressMerkmale,
      ],
    });
  }

  return out;
};

/** Gehört ein vorhandenes Kind zur Art? (Öltank: Umwelt-Risiko mit „Öltank“-Namen.) */
export const passtZurArt = (kind: VorhandenesKind, art: UnterRisikoArt): boolean => {
  const typ = text(kind.riskType).toLowerCase();
  if (art === "photovoltaik") return typ === "photovoltaik";
  return typ === "umwelt" && text(kind.name).startsWith(OELTANK_NAME_PREFIX);
};

/**
 * Ordnet jedem Entwurf ein vorhandenes Kind gleicher Art zu (`existingId`) oder
 * `null` (neu anlegen). Jedes Kind wird höchstens einmal vergeben; nicht
 * zugeordnete Kinder bleiben unangetastet.
 */
export const gleicheMitKindernAb = (
  entwuerfe: UnterRisikoEntwurf[],
  kinder: VorhandenesKind[],
): Array<{ entwurf: UnterRisikoEntwurf; existingId: number | null }> => {
  const vergeben = new Set<number>();
  return entwuerfe.map((entwurf) => {
    const treffer = kinder.find((k) => !vergeben.has(k.id) && passtZurArt(k, entwurf.art));
    if (treffer) vergeben.add(treffer.id);
    return { entwurf, existingId: treffer ? treffer.id : null };
  });
};

/**
 * Merkmale für das Aktualisieren eines vorhandenen Kinds: die von hier
 * gepflegten Schlüssel ({@link VERWALTETE_KEYS}) kommen aus dem Gebäude (ein
 * geleertes Feld verschwindet auch am Kind), von Hand am Kind ergänzte
 * Merkmale bleiben — der PUT ersetzt sonst ALLE Merkmale.
 */
export const fuehreMerkmaleZusammen = (
  vorhanden: Array<{ nameBezeichnung?: string | null; riskCharacteristicValue?: string | null }>,
  entwurf: Pick<UnterRisikoEntwurf, "art" | "characteristics">,
): Merkmal[] => {
  const neu = entwurf.characteristics;
  const neueKeys = new Set<string>([
    ...VERWALTETE_KEYS[entwurf.art],
    ...neu.map((m) => m.nameBezeichnung),
  ]);
  const behalten = (vorhanden ?? [])
    .map((m) => ({
      nameBezeichnung: text(m.nameBezeichnung),
      riskCharacteristicValue: String(m.riskCharacteristicValue ?? ""),
    }))
    .filter((m) => m.nameBezeichnung !== "" && !neueKeys.has(m.nameBezeichnung));
  return [...behalten, ...neu];
};
